import * as React from 'react';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { Accordion } from '@leagueplatform/genesis-core-alpha';
import {
  type GDSHeadingLevel,
  type GDSHeadingSize,
} from '@leagueplatform/genesis-core';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { addToActionAnalytics } from '../../utils/addToActionAnalytics';
import { type MasonryDriverAction } from '../../types/masonry-driver-node-actions';

export const NODE_TYPE_ACCORDION_ITEM = 'accordionItem';

type MasonryAccordionItemProps = {
  heading: string;
  headingLevel: GDSHeadingLevel;
  headingSize?: GDSHeadingSize;
  isExpanded: boolean;
  dividerPosition?: 'top' | 'bottom' | 'both' | 'none';
};

export type MasonryAccordionItemNode = MasonryEngineNode<
  typeof NODE_TYPE_ACCORDION_ITEM,
  MasonryAccordionItemProps,
  {
    onToggle?: MasonryDriverAction;
  },
  {
    items: MasonryEngineNode<string, any>[];
  }
>;

export type MasonryAccordionItemRendererProps =
  MasonryEngineNodeRendererProps<MasonryAccordionItemNode>;

export const MasonryAccordionItemRenderer = ({
  isExpanded,
  heading,
  headingLevel,
  headingSize = 'lg',
  onToggle,
  items,
  rootElementRef,
  className,
  engineClassName,
  css,
  locatorId,
  dividerPosition,
}: MasonryAccordionItemRendererProps) => {
  const invokeAction = useInvokeAction();

  const isExpandedRef = React.useRef(isExpanded);
  isExpandedRef.current = isExpanded;

  const clickHandler = React.useCallback(() => {
    invokeAction({
      type: 'setNodeState',
      namespace: 'default',
      payload: {
        state: {
          isExpanded: !isExpandedRef.current,
        },
      },
    });
    if (onToggle) {
      invokeAction(
        addToActionAnalytics(onToggle, { isExpanded: !isExpandedRef.current }),
      );
    }
  }, [onToggle, invokeAction]);

  if (!items || !items.length) return null;

  return (
    <Accordion
      onClick={clickHandler}
      isExpanded={isExpanded}
      heading={heading}
      headingLevel={headingLevel}
      headingSize={headingSize}
      dividerPosition={dividerPosition}
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      {items}
    </Accordion>
  );
};
